import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import {
  CELSIUS_ID, FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import { formatTimeDifferenceToDisplayDuration, formatTimestampToFullDate } from '../../../../utility/DateUtility';
import getChannelValueWithUnitsFromChannelKeyObjectString
  from '../../../common/ChannelValueWithUnitsFromChannelKeyObject/getChannelValueWithUnitsFromChannelKeyObjectString';
import { getOperatorTypeFromSymbol } from '../../../xlalerts/alertforms/AlertFormUtility';
import {
  getAlertSource,
  isSpecialChannel,
  getSpecialConditionLabel,
} from '../../../../utility/alertDisplayUtils';

/* istanbul ignore next */
const AlertHistoryTableRow = ({ alert, columnVisibility, userPreferences }) => {
  const { timezone, temperatureScale } = userPreferences;

  const duration = formatTimeDifferenceToDisplayDuration(
    alert.triggerTimestamp,
    alert.clearTimestamp || moment().unix(),
  );

  const channelKey = new ChannelKey(alert.channelKey);
  const channel = ChannelService.getChannelGroupChannel(
    channelKey.channelGroupType,
    channelKey.channelIndex,
    channelKey.channelType,
  );

  const isRAMAXDevice = alert.deviceType === 'RAMAX';
  const source = getAlertSource(alert, channel, isRAMAXDevice, alert.componentName || '');

  const startOperatorTypeId = getOperatorTypeFromSymbol(alert.startOperator) || alert.startOperator;
  const { hysteresisMultiplier } = OperatorTypeDetails[startOperatorTypeId] || 1;

  const special = isSpecialChannel(channel);

  const renderValue = (value) => getChannelValueWithUnitsFromChannelKeyObjectString(
    temperatureScale,
    channelKey,
    value,
    alert.customUnit,
  );

  const triggerDisplayValue = special
    ? getSpecialConditionLabel(channel, alert, false, startOperatorTypeId)
    : `${alert.startOperator} ${renderValue(alert.startThreshold)}`;

  const clearDisplayValue = special
    ? getSpecialConditionLabel(channel, alert, false, startOperatorTypeId)
    : `${alert.clearOperator} ${renderValue(alert.clearThreshold + (channel.hysteresisBuffer * hysteresisMultiplier))}`;

  const renderClearDisplayValue = () => {
    if (alert.clearValue === 'Alert Edited') return alert.clearValue;
    if (alert.clearValue == null) return '--';
    if (!(channel instanceof BooleanChannel)) return clearDisplayValue;
    return null;
  };

  const tdStyle = {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const beganEndedTitle = `${formatTimestampToFullDate(alert.triggerTimestamp, timezone)} - ${
    alert.clearTimestamp != null
      ? formatTimestampToFullDate(alert.clearTimestamp, timezone)
      : 'In Alarm'
  }`;

  const triggerTitle = `${renderValue(alert.triggerValue)}${
    !(channel instanceof BooleanChannel) ? ` ${triggerDisplayValue}` : ''
  }`;

  const clearValuePart = (alert.clearValue != null && alert.clearValue !== 'Alert Edited')
    ? `${renderValue(alert.clearValue)} `
    : '';
  const clearTitle = `${clearValuePart}${renderClearDisplayValue() || ''}`.trim();

  const actionsTitle = [
    alert.emailActionCount > 0 ? `${alert.emailActionCount} email` : null,
    alert.textActionCount > 0 ? `${alert.textActionCount} text` : null,
    alert.postActionCount > 0 ? `${alert.postActionCount} post` : null,
    alert.realSmsActionCount > 0 ? `${alert.realSmsActionCount} SMS` : null,
    alert.pushNotificationActionCount > 0 ? `${alert.pushNotificationActionCount} push` : null,
  ].filter(Boolean).join(', ');

  return (
    <tr>
      <ConditionalRender show={columnVisibility.BeganEnded}>
        <td style={tdStyle} title={beganEndedTitle}>
          <span>{formatTimestampToFullDate(alert.triggerTimestamp, timezone)}</span>
          {' - '}
          <span>
            {alert.clearTimestamp != null
              ? formatTimestampToFullDate(alert.clearTimestamp, timezone)
              : 'In Alarm'}
          </span>
        </td>
      </ConditionalRender>

      <ConditionalRender show={columnVisibility.Duration}>
        <td style={tdStyle} title={duration}><span>{duration}</span></td>
      </ConditionalRender>

      <ConditionalRender show={columnVisibility.Source}>
        <td style={tdStyle} title={source}>{source || '—'}</td>
      </ConditionalRender>

      <ConditionalRender show={columnVisibility.Trigger}>
        <td style={tdStyle} title={triggerTitle}>
          <b className="text-danger">
            <span>{renderValue(alert.triggerValue)}</span>
          </b>
          {!(channel instanceof BooleanChannel) && (
            <>
              {' '}
              <span>{triggerDisplayValue}</span>
            </>
          )}
        </td>
      </ConditionalRender>

      <ConditionalRender show={columnVisibility.Clear}>
        <td style={tdStyle} title={clearTitle}>
          {(alert.clearValue != null && alert.clearValue !== 'Alert Edited') && (
            <span>
              <b className="text-success">
                <span>{renderValue(alert.clearValue)}</span>
              </b>
              {' '}
            </span>
          )}
          <span>{renderClearDisplayValue()}</span>
        </td>
      </ConditionalRender>

      <ConditionalRender show={columnVisibility.Actions}>
        <td style={tdStyle} title={actionsTitle}>
          {alert.emailActionCount > 0 && (
            <span>
              <span>{alert.emailActionCount}</span>
              {' '}
              <Icon icon="envelope" />
              {' '}
            </span>
          )}
          {alert.textActionCount > 0 && (
            <span>
              <span>{alert.textActionCount}</span>
              {' '}
              <Icon icon="laptop-mobile" />
              {' '}
            </span>
          )}
          {alert.postActionCount > 0 && (
            <span>
              <span>{alert.postActionCount}</span>
              {' '}
              <Icon icon="square-arrow-up-right" />
              {' '}
            </span>
          )}
          {alert.realSmsActionCount > 0 && (
            <span>
              <span>{alert.realSmsActionCount}</span>
              {' '}
              <Icon icon="message-dots" />
              {' '}
            </span>
          )}
          {alert.pushNotificationActionCount > 0 && (
            <span>
              <span>{alert.pushNotificationActionCount}</span>
              {' '}
              <Icon icon="mobile-notch" />
              {' '}
            </span>
          )}
        </td>
      </ConditionalRender>
    </tr>
  );
};

AlertHistoryTableRow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  alert: PropTypes.object.isRequired,
  columnVisibility: PropTypes.shape({
    BeganEnded: PropTypes.bool,
    Duration: PropTypes.bool,
    Source: PropTypes.bool,
    Trigger: PropTypes.bool,
    Clear: PropTypes.bool,
    Actions: PropTypes.bool,
  }),
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    timezone: PropTypes.string,
  }),
};

AlertHistoryTableRow.defaultProps = {
  columnVisibility: {
    BeganEnded: true,
    Duration: true,
    Source: true,
    Trigger: true,
    Clear: true,
    Actions: true,
  },
  userPreferences: {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
    timezone: 'America/New_York',
  },
};

export default AlertHistoryTableRow;
